/**
 * All Sports Arena (ASA): a stand-in brand on top of the DFC Fitness Village design.
 *
 * ASA's company record carries `theme: 'allSportsArenaTheme'`. `useCompany` hands the rest of the
 * app `theme: 'dfcVillageTheme'` plus `brandVariant: 'asa'` (see `aliasAsaTheme`), so every
 * component renders DFC's village layout untouched and only this brand layer reads the flag:
 *   palette  ASA_PALETTE, written to the `--village-*` properties `DFC_PALETTE` falls back from,
 *            per light / dark mode; ASA_SURFACE for the button and selected-state colours
 *   copy     ASA_COPY_EN, returned by `useFitnessVillageCopy`
 *   artwork  none yet: the home hides DFC's stats and village lockups
 *
 * A shortcut until the revamp gives ASA a theme of its own. `'allSportsArenaTheme'` is kept out of
 * `ThemeVariant` on purpose: adding it would force a key into all 42 `variantComponents` records.
 */
import type { Company } from '../hooks/useCompany';
import { BrandPalette, PALETTE_CSS_VAR, hexToRgbChannels, withAlpha } from './dfcBrand';
import { DfcSurface, getDfcSurface } from './dfcSurface';
import { VILLAGE_MODE_ATTRIBUTE } from '../context/DfcThemeModeContext';
import { FITNESS_VILLAGE_COPY_EN, FitnessVillageCopy } from './fitnessVillageCopy';

export const ASA_THEME = 'allSportsArenaTheme';

/** 'club15': see `club15Brand.ts`. */
export type BrandVariant = 'asa' | 'club15';

export const isAsaCompany = (company?: Pick<Company, 'brandVariant'> | null): boolean =>
  company?.brandVariant === 'asa';

/** ASA's record as the app should see it: the DFC village theme, flagged as ASA. Others pass through. */
export const aliasAsaTheme = (company: Company): Company =>
  (company.theme as string) === ASA_THEME
    ? { ...company, theme: 'dfcVillageTheme', brandVariant: 'asa' }
    : company;

/** ASA brand colours, sampled from ASA's poster (13/10). Basel: blue and white. */
export const ASA_COLOR = {
  royalBlue: '#0044DC',
  navy: '#012140',
  /** The photo tint on the poster; used only as a gradient end stop. */
  navyLight: '#06325F',
  white: '#FFFFFF',
  /** Muted text on white / the soft grey fill: 5.67:1 / 5.06:1 (the shared grey #A4ABB8 was 2.3:1). */
  mutedOnLight: '#5C6878',
  /** Muted text on navy cards: 6.1:1 (the shared 48% white was 4.2:1). */
  mutedOnDark: '#A7B3C2',
  /** Destructive text on navy cards: 6.4:1 (DFC's #C82E34 is 2.4:1 there). */
  errorOnDark: '#FF9B9B',
  /** Error toast fill under white text: 6.6:1. */
  errorFill: '#B42318',
  /** The header bar on allsportsarena.com (light mode). White on it: 5.56:1. */
  siteBlue: '#0056FF',
  /** On the poster ("MIND.", the date) but not in use: Basel asked for blue and white. */
  oliveLime: '#A8A80C',
} as const;

/*
 * Contrast (WCAG 2.1):
 *   royal blue on white 7.34:1, white on royal blue 7.34:1   AA, any size
 *   navy on white 16.2:1                                      AA, any size
 *   royal blue on navy 2.21:1                                 FAILS, even large text and UI marks
 * So in dark mode no text or mark is royal blue on navy: accents and marks turn white, and royal
 * blue appears only as a fill (primary buttons, selected chips and tabs) carrying white text.
 */

/**
 * ASA's palette per mode, in DFC's slots: blue = primary accent, green = secondary accent
 * (underlines, badges, dark-mode links and focus), black = ink and the dark page, white = paper.
 */
export const ASA_PALETTE: Record<'light' | 'dark', BrandPalette> = {
  light: {
    blue: ASA_COLOR.royalBlue,
    green: ASA_COLOR.royalBlue,
    black: ASA_COLOR.navy,
    white: ASA_COLOR.white,
    blueDark: ASA_COLOR.navy,
    greenDark: ASA_COLOR.navyLight,
  },
  dark: {
    blue: ASA_COLOR.white,
    green: ASA_COLOR.white,
    black: ASA_COLOR.navy,
    white: ASA_COLOR.white,
    blueDark: ASA_COLOR.navyLight,
    greenDark: ASA_COLOR.navyLight,
  },
};

/**
 * ASA's overrides of the village surface scale (see `useDfcSurface`): royal blue fills with white
 * text for the primary button and the selected state, in both modes.
 */
export const ASA_SURFACE: Record<'light' | 'dark', Partial<DfcSurface>> = {
  light: {
    ink3: ASA_COLOR.mutedOnLight,
    accentText: ASA_COLOR.royalBlue,
    accentMark: ASA_COLOR.royalBlue,
    ctaBg: ASA_COLOR.royalBlue,
    ctaFg: ASA_COLOR.white,
    ctaSub: withAlpha(ASA_COLOR.white, 0.72),
    ctaArrow: ASA_COLOR.white,
    onBg: ASA_COLOR.royalBlue,
    onFg: ASA_COLOR.white,
  },
  dark: {
    ink3: ASA_COLOR.mutedOnDark,
    // Card and field edges: the shared 16% / 30% white all but vanish on navy.
    border: withAlpha(ASA_COLOR.white, 0.28),
    borderStrong: withAlpha(ASA_COLOR.white, 0.5),
    accentText: ASA_COLOR.white,
    accentMark: ASA_COLOR.white,
    ctaBg: ASA_COLOR.royalBlue,
    ctaFg: ASA_COLOR.white,
    ctaSub: withAlpha(ASA_COLOR.white, 0.72),
    ctaArrow: ASA_COLOR.white,
    onBg: ASA_COLOR.royalBlue,
    onFg: ASA_COLOR.white,
  },
};

/**
 * The light-mode header bar, as on allsportsarena.com: solid site blue with white logo, nav and
 * text, and a white sign-in pill with blue text. Built on ASA's dark surface (white text) with
 * the bar's own colours on top. Muted nav text is 90% white: 4.78:1 on the blue.
 */
export const ASA_HEADER_SURFACE_LIGHT: DfcSurface = {
  ...getDfcSurface('dark'),
  ...ASA_SURFACE.dark,
  headerBg: ASA_COLOR.siteBlue,
  headerBorder: 'transparent',
  ink2: withAlpha(ASA_COLOR.white, 0.9),
  ink3: withAlpha(ASA_COLOR.white, 0.9),
  track: withAlpha(ASA_COLOR.white, 0.18),
  ctaBg: ASA_COLOR.white,
  ctaFg: ASA_COLOR.siteBlue,
  onBg: ASA_COLOR.white,
  onFg: ASA_COLOR.siteBlue,
  logoBg: 'transparent',
  logoPad: '0px',
};

const ASA_STYLE_ID = 'asa-brand-palette';

const paletteRule = (selector: string, palette: BrandPalette) =>
  `${selector}{${(Object.keys(PALETTE_CSS_VAR) as (keyof BrandPalette)[])
    .map(
      key =>
        `${PALETTE_CSS_VAR[key]}:${palette[key]};${PALETTE_CSS_VAR[key]}-rgb:${hexToRgbChannels(palette[key])};`
    )
    .join('')}}`;

/** The stylesheet that repaints the village for ASA: light on `:root`, dark under the mode attribute. */
const DARK = `:root[${VILLAGE_MODE_ATTRIBUTE}='dark']`;

/**
 * ASA rules for shared pieces the village surface scale does not reach: the generic promo-code
 * field, react-toastify's toasts and the cart countdown strip. A shortcut, like the rest of this
 * file, until the revamp gives ASA its own theme; it only exists while the company is ASA.
 */
const purchaseFlowCss = (): string => {
  const { royalBlue, white, navy, errorFill } = ASA_COLOR;
  return [
    // Promo code: a readable field and a royal-blue Apply, in both modes.
    `.package-code-input .MuiOutlinedInput-input::placeholder{color:${ASA_COLOR.mutedOnLight};opacity:1;}`,
    `.package-code-input .package-code-button{background:${royalBlue};color:${white};border-radius:999px;padding:4px 16px;}`,
    `.package-code-input .package-code-button.Mui-disabled{background:${withAlpha(royalBlue, 0.45)};color:${white};}`,
    `${DARK} .package-code-input{color:${white};background:${withAlpha(white, 0.06)};}`,
    `${DARK} .package-code-input .MuiOutlinedInput-notchedOutline{border-color:${withAlpha(white, 0.5)};}`,
    `${DARK} .package-code-input .MuiOutlinedInput-input::placeholder{color:${ASA_COLOR.mutedOnDark};}`,
    // Toasts: royal-blue success and a darker red error, both under white text. react-toastify's
    // coloured theme reads these properties; `:root:root` outranks its own `:root`.
    `:root:root{--toastify-color-success:${royalBlue};--toastify-color-error:${errorFill};}`,
    // Light-mode header: styles there that read the palette directly (the active-nav underline,
    // the cart badge, focus rings) take its dark, white-accent values, on the blue bar only.
    paletteRule(`:root:not([${VILLAGE_MODE_ATTRIBUTE}='dark']) .MuiAppBar-root`, ASA_PALETTE.dark),
    // Native controls (the terms checkbox, selects) draw their dark versions on navy.
    `${DARK}{color-scheme:dark;}`,
    // Cart countdown: a royal-blue band so it reads as part of the booking, not page chrome.
    `[data-testid='cart-hold-strip']{background:${royalBlue};}`,
    `[data-testid='cart-hold-strip'] span{color:${white};}`,
    `${DARK} [data-testid='cart-hold-strip']{border-color:${navy};}`,
  ].join('');
};

export const asaPaletteCss = (): string =>
  // Royal blue on a darkened photo is unreadable, so accents on photos are white in both modes.
  `:root{--village-on-photo:${ASA_COLOR.white};--village-on-accent:${ASA_COLOR.white};}` +
  `${DARK}{--village-on-accent:${ASA_COLOR.navy};--village-error:${ASA_COLOR.errorOnDark};--village-error-rgb:${hexToRgbChannels(ASA_COLOR.errorOnDark)};}` +
  paletteRule(':root', ASA_PALETTE.light) +
  paletteRule(DARK, ASA_PALETTE.dark) +
  purchaseFlowCss();

/**
 * Adds (ASA) or removes (everyone else) the `--village-*` stylesheet. Without it every
 * `DFC_PALETTE` value resolves to its DFC hex, so for DFC this changes nothing.
 */
/*
 * The loader has to be chosen before the company record (and so its theme) has loaded. ASA's
 * known company ids, plus any id this browser has already seen load as ASA, are the hint. It
 * only picks the loader: every ASA behaviour still keys off the theme.
 */
const ASA_KNOWN_COMPANY_IDS = [
  '0d7bcff519404027bd4d7bb2eb174b7f', // production
  '8992ebfb1c1a4855973bb0d97be5d5f4', // staging (skmd-test)
];
const ASA_SEEN_IDS_KEY = 'asaCompanyIds';

const readSeenAsaIds = (): string[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ASA_SEEN_IDS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const isLikelyAsaCompanyId = (companyId?: string | null): boolean =>
  Boolean(companyId) &&
  (ASA_KNOWN_COMPANY_IDS.includes(companyId as string) ||
    readSeenAsaIds().includes(companyId as string));

const rememberAsaCompanyId = (companyId?: string) => {
  if (!companyId || isLikelyAsaCompanyId(companyId)) return;
  try {
    window.localStorage.setItem(ASA_SEEN_IDS_KEY, JSON.stringify([...readSeenAsaIds(), companyId]));
  } catch {
    // Storage unavailable: the next visit shows the generic loader, nothing more.
  }
};

export const ASA_LOADER_LABEL = 'Loading All Sports Arena';

export const applyBrandPalette = (
  company?: (Pick<Company, 'brandVariant'> & { id?: string }) | null
) => {
  const existing = document.getElementById(ASA_STYLE_ID);
  if (!isAsaCompany(company)) {
    existing?.remove();
    return;
  }
  rememberAsaCompanyId(company?.id);
  const style = existing ?? document.head.appendChild(document.createElement('style'));
  style.id = ASA_STYLE_ID;
  style.textContent = asaPaletteCss();
};

const DFC = FITNESS_VILLAGE_COPY_EN;

// PLACEHOLDER until ASA copy arrives. No prices, counts, rules or "free" claims: those are ASA's.
const BRAND = 'All Sports Arena';

/** ASA's English, in DFC's shape. Only what names DFC, its event or its rules is replaced. */
export const ASA_COPY_EN: FitnessVillageCopy = {
  ...DFC,
  home: {
    ...DFC.home,
    dateline: BRAND,
    /** Not drawn: ASA's home keeps its "Book your game" hero. Set so no DFC name is inherited. */
    heroTitle: { lead: BRAND, tail: '' },
    heroLead: 'Book your',
    heroAccent: 'game',
    intro: 'Join a class or book a court.',
    chooseVillage: 'Choose a venue',
    classesAction: { ...DFC.home.classesAction, sub: 'See the timetable' },
    courtsAction: {
      label: 'Courts',
      sub: 'Book a slot',
      aria: (venue: string) => `Book a court at ${venue}`,
    },
    emptyVillages: 'Venues open for booking soon.',
    stats: { title: BRAND, footnote: '' },
    lockups: { title: 'Venues', alt: (venue: string) => venue },
  },
  homeStats: [],
  page: {
    ...DFC.page,
    // One venue, so the back link goes home rather than to a list of venues.
    allVillages: 'Home',
    switchVillage: 'Switch venue',
    classesIntro: 'Pick a class and book your spot.',
    classesNote: 'Places are limited, so book ahead.',
    /*
     * The tab, nav and labels say "Courts" (Basel, 14/10). Each card's count names its own unit
     * (asaSpaceCount), so a pool's row never reads "court".
     */
    courtsLead: 'Book a court at',
    tabs: { classes: 'Classes', courts: 'Courts', ariaLabel: 'Classes or courts' },
    sports: 'Courts',
    seeAllSports: (count: number) => `See all ${count} courts`,
    courtsIntro: 'Pick a court, a day and a time.',
    courtHours: (hours: string) => `Bookings: ${hours}`,
    loadError: 'We could not load this venue. Try again.',
    mapComingSoon: 'Venue map coming soon.',
    villageTitle: (venue: string) => venue,
    unknownVillage: 'We could not find this venue.',
    classDetails: { ...DFC.page.classDetails, dropIn: 'Tap the class to book your spot.' },
  },
  auth: {
    ...DFC.auth,
    eyebrow: BRAND,
    signUp: { ...DFC.auth.signUp, body: 'One account for all your bookings.' },
  },
  header: {
    ...DFC.header,
    logoAlt: BRAND,
    home: `${BRAND} home`,
    menuFootnote: BRAND,
    nav: { ...DFC.header.nav, bookings: 'Courts' },
  },
  checkout: {
    ...DFC.checkout,
    backToVillages: 'Home',
    emptyCartAction: 'Browse classes and courts',
  },
  confirmation: { ...DFC.confirmation, backHome: 'Home' },
  profile: {
    ...DFC.profile,
    checkInBody: 'Show this code at the entrance.',
    browseVillages: 'Browse classes and courts',
  },
  signup: { ...DFC.signup, eyebrow: BRAND },
  cancellation: { ...DFC.cancellation, backHome: 'Home' },
  loader: { label: `Loading ${BRAND}` },
};

/**
 * The count under an ASA space, in that space's own unit: "2 courts", "3 pitches". A single
 * unit, or a space with no countable unit (a pool), shows no count. Matched on the service name
 * because no service field says what a space is.
 */
export const asaSpaceCount = (serviceName: string, units: number): string | undefined => {
  if (units <= 1) return undefined;
  if (/football|soccer|cricket|rugby/i.test(serviceName)) return `${units} pitches`;
  if (
    /padel|squash|tennis|badminton|basketball|volleyball|pickleball|multipurpose|court/i.test(
      serviceName
    )
  )
    return `${units} courts`;
  return undefined;
};

/** Booking a class from the timetable (ASA only; DFC's classes are drop-in). */
export const ASA_BOOKING_COPY = {
  /** Accessible name of a class row: "Book Reformer Basics, 08:00" */
  bookAria: (title: string, time: string) => `Add ${title}, ${time} to cart`,
  fullAria: (title: string, time: string) => `${title}, ${time}, full`,
  detailsAria: (title: string) => `Details of ${title}`,
  booking: 'Booking…',
  classFull: 'This class is full. Pick another time.',
  bookError: 'We could not book this class. Try another time.',
};

/** The ASA home (`AsaHome.tsx`). Nothing here states a price, a count, an hour or a facility. */
export const ASA_HOME_COPY = {
  heroLead: 'Book your',
  /** Shown inside ASA's square brackets: BOOK YOUR [GAME] */
  heroWord: 'game',
  intro: 'Join a class or book a court.',
  bookSpace: 'Book a court',
  findClass: 'Find a class',
  comingUp: 'Coming up',
  today: 'Today',
  tomorrow: 'Tomorrow',
  /** Accessible name of a coming-up card: "Add Morning Flow, today 07:00 to cart" */
  bookAria: (title: string, day: string, time: string) => `Add ${title}, ${day} ${time} to cart`,
  spotsLeft: (count: number) => (count === 1 ? '1 spot left' : `${count} spots left`),
  full: 'Full',
  welcome: (name?: string) => (name ? `Welcome back, ${name}` : 'Welcome back'),
  welcomeBody: 'Your bookings are in your profile.',
  myBookings: 'My bookings',
};

/**
 * Headings and tab titles of ASA's venue page. No branch name (Basel, 14/10): ASA has one
 * venue, and its branch is called "Dubai" in production. The word in brackets takes the home
 * hero's highlighted treatment.
 */
export const ASA_PAGE_HEADINGS = {
  classes: { lead: 'How are we', word: 'moving', tail: 'today?', title: 'Classes' },
  courts: { lead: 'Where will you', word: 'play', tail: 'today?', title: 'Courts' },
  gym: { lead: 'Ready to', word: 'train', tail: 'today?', title: 'Gym' },
};

/** ASA's nav beyond the village three (Home, Classes, Courts). */
export const ASA_NAV = {
  gym: 'Gym',
  offers: 'Vouchers & Memberships',
};

/** The Gym tab of the venue page. No rule or price is stated here: the cards carry the data. */
export const ASA_GYM_COPY = {
  intro: 'Pick a day and a time, and pay per session.',
  picker: 'Gym',
  membershipLead: 'Train often?',
  /** "30 sessions · 30 days" */
  membershipFacts: (sessions: number, days: number) =>
    [sessions > 0 ? `${sessions} sessions` : '', days > 0 ? `${days} days` : '']
      .filter(Boolean)
      .join(' · '),
  viewMembership: 'View membership',
};

/**
 * Vouchers & Memberships (`AsaOffers.tsx`). Figures come from the company's own vouchers; the
 * only computed line is the difference between what a credit voucher costs and what it gives.
 */
export const ASA_OFFERS_COPY = {
  lead: 'Play more,',
  word: 'pay less',
  intro: 'Prepaid credit and memberships, bought online.',
  credits: 'Credit vouchers',
  memberships: 'Memberships',
  pay: 'Pay',
  get: 'Get',
  /** "AED 50 extra" */
  extra: (amount: string) => `${amount} extra`,
  /** "30 sessions · 30 days" */
  membershipFacts: (sessions: number, days: number) =>
    [sessions > 0 ? `${sessions} sessions` : '', days > 0 ? `${days} days` : '']
      .filter(Boolean)
      .join(' · '),
  buy: 'Buy',
  buyAria: (name: string) => `Buy ${name}`,
  empty: 'No vouchers or memberships are on sale right now.',
};

/** ASA's voucher / membership confirmation (`AsaPackageConfirmation.tsx`). */
export const ASA_CONFIRMATION_COPY = {
  title: 'Confirmed',
  lead: "You're",
  word: 'in',
  sentTo: (email: string) => `We've sent the details to ${email}.`,
  sent: "We've sent the details to your email.",
  credit: 'Credit',
  membership: 'Membership',
  /** "30 sessions · 30 days" */
  facts: (sessions: number, days: number) =>
    [sessions > 0 ? `${sessions} sessions` : '', days > 0 ? `${days} days` : '']
      .filter(Boolean)
      .join(' · '),
  validUntil: (date: string) => `Valid until ${date}`,
  paid: 'Paid',
  codeLabel: 'Your code',
  copy: 'Copy code',
  copied: 'Copied',
  useCode: 'Use this code at checkout on your next booking.',
  home: 'Home',
};

/** Headings of ASA's purchase step, by what is being bought. */
export const ASA_PURCHASE_HEADINGS = {
  credit: { lead: 'Your', word: 'voucher' },
  membership: { lead: 'Your', word: 'membership' },
};

/** Durations and prices on ASA's courts and gym (`useAsaCourtPricing`). */
export const ASA_PRICE_COPY = {
  /** "AED 850"; "Free" at zero */
  price: (currency: string, amount: number) =>
    amount === 0 ? 'Free' : `${currency} ${amount.toLocaleString('en-US')}`,
  minutes: (minutes: number) => `${minutes} min`,
  peak: 'Peak',
  offPeak: 'Off-peak',
  /** "Peak times: weekday evenings and weekends" is ASA's to say; the legend states the marker only */
  peakLegend: 'Peak time',
};

/** The step-by-step court and gym booking (`AsaCourtFlow.tsx`). */
export const ASA_COURT_FLOW_COPY = {
  steps: { court: 'Court', gym: 'Gym', day: 'Day', duration: 'Duration', time: 'Time' },
  /** "Padel · 90 min · 18:00 · AED 900 · Peak" pieces */
  slotAria: (time: string, peak: boolean) => (peak ? `${time}, peak` : time),
  pickTime: 'Pick a time',
};

/** ASA's multi-booking cart: add, stay, and go to the cart when ready. */
export const ASA_CART_COPY = {
  addToCart: 'Add to cart',
  /** "Go to cart (2)" */
  goToCart: (count: number) => `Go to cart (${count})`,
  /** The classes tab's cart bar: "2 in your cart" */
  inCart: (count: number) => (count === 1 ? '1 in your cart' : `${count} in your cart`),
};
